import React from 'react';
import WName, {WArticle} from '../WName';
import PL from './ProblemLink';
import AL from '../angles/AngleLink';
import QL from '../faq/QuestionLink';
import NW from '../neologisms/NewWord';
import OML from '../ormaybe/OrMaybeLink';
import LRL from '../longreads/LongReadLink';
import {E1, E2, TLDR, toosin} from '../common';

// Confusing substance with representation. 
export const form = {
  name: "form",
  alternatives: "substance,representation,manifestation",
  parentName: "our confusing form with substance",
  prelude: <span>
    But if it <i>looks</i> ‘trustworthy’…
  </span>,
  header: <span>
    We are confusing <i>substance</i> with <i>representation</i>
  </span>,
  outside: <span>
    To a frankly <i>terrifying</i> degree, cf. <LRL>nihilism</LRL>. The first step to learning to <PL>trust ourself</PL> is to <LRL>understand</LRL>(and <i><LRL name="game">agree on</LRL></i>) what is ‘<LRL>real</LRL>’.
  </span>,
  preview: <div className="problem-preview">
    <p>If a <i><NW>zyzzyva</NW></i> says it, then it comes from an <i>insect</i>, so it is Automatically<sup>®</sup> wrong.</p>
    <p>The problem with confusing substance and representation is a symptom of humanity’s <NW>polygnosticity</NW>,the <AL name="handicap">limited</AL> <NW>co-fo</NW> of each <NW>agency</NW>-laden (or <AL name="sleeping">potentially-laden</AL>) <AL>atom</AL>/<NW word="perat">פרט</NW>, our <PL>radical mistrust</PL> of each <NW>heterognostic</NW>-‘<QL>other</QL>’… and, frankly, <NW word="jorm">Jörmungandr</NW>.</p>
    <p>Like <QL>gnosticity</QL>, this one <E2 suffix="s" /> a <PL name="sweeping">whole bunch</PL>.</p>
  </div>,
  text: <div className="problem-text">
    <p>The reason that the <AL name="religion.">Hindu</AL> school of द्वैत वेदान्त (Dvaita Vedānta: dualistic conclusion of the Vedas) perceives ‘<LRL>reality</LRL>’ (<NW word="haklal">הכלל</NW>, or <AL name="religions.hindu.brahman">ब्रह्मन्</AL>) as two <i>separate</i> things – पुरुष (purusha: pure consciousness) and प्रकृति (prakriti: matter) – is that there are two distint <i>components</i> to <LRL>reality</LRL>. It can be encapsulated in the <AL name="fields.science">QM/GR divide</AL> (QM = प्रकृति/prakriti, GR = पुरुष/purusha), cf. the <QL>metaphor</QL> with <AL>Barbie and Ken</AL> (and the <QL name="language">biblioteca</QL>).</p>
    <p>It also ties into confusing a mathematical ‘formalism’ with Truth<sup>®</sup> (as externally <PL name="arbitrer">imposed</PL>); if you can say it in <i>formal language</i> it must be True<sup>®</sup>. <PL name="ourself">Without realising</PL> that the foundations of the ‘formalism’ may be wrong. If you have a worldview that claims 2 + 2 = 5 – for <NW>heterognostic</NW> <i>me</i> – if I really <i><LRL>believe</LRL></i> it does, then it <OML name="matters">doesn’t matter</OML> what that formalism <i>can</i> say… because it’s <QL name="consistent">inconsistent</QL>.</p>
    <p>It could claim that unicorns exist on Mars right now and prove that true. It’s called the ‘principle of explosion’.</p>
    <p>[Under construction]</p>
    <hr />
      <p>(It’s basically अनिमित्त/animitta: signlessness.)</p>
  </div>,
  sublist: [
  {
    name: "feel",
    alternatives: "feel good,feel guud",
    prelude: <span>
      We don’t want <QL>truth</QL>. We just want to <i>feel good</i> while <LRL name="scope">the universe</LRL> burns.
    </span>,
    header: <span>
      We are drugged up on <i>anaesthetics</i>.
    </span>,
    preview: <div className="problem-preview">
      <p>If you <QL name="salvation">imagine</QL> <LRL name="gaia">all of Earth</LRL> as a <QL>body</QL> – and a <AL>sick</AL> one at that – then what’s not <QL name="salvation.">actually thinking</QL>, and outsourcing all our problems to ‘<NW>ziggurats</NW>’?</p>
      <p>It’s basically the planetary equivalent to drinking ourself to <AL name="peace">death</AL>.</p>
      <p>Let’s maybe <i>not</i>, humanity?</p>
    </div>,
    text: <div className="problem-text">
      <p>It ties into <i><PL>comfort</PL></i> as the real Opium des Volkes. <AL>Religion</AL> isn’t <PL>evil</PL>. ‘Evil’ is <i>refusing to <QL name="consciousness">consciously</QL> act</i> (cf. <NW>agency</NW>) and {toosin}.</p>
      <p>[Under construction, maybe, or maybe it's just the <NW>LOC</NW>.]</p>
    </div>,
  },
  ],
};
